import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import slaService, {
  SLAPolicy,
  SLARule,
  BusinessHours,
  CreatePolicyData,
  UpdatePolicyData,
  Holiday
} from '@/services/sla.service';
import organizationsService from '@/services/organizations.service';
import { Building2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Clock,
  AlertCircle,
  Plus,
  Edit,
  Trash2,
  Save,
  Calendar,
  BarChart3,
  Settings,
  CheckCircle,
  XCircle,
  CalendarDays,
  RefreshCw,
} from 'lucide-react';
import { formatCustom } from '@/lib/format';
import { useTranslation } from 'react-i18next';

const priorities = [
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' },
  { value: 'high', label: 'High', color: 'bg-gray-900 text-white' },
  { value: 'normal', label: 'Normal', color: 'bg-blue-100 text-blue-800' },
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800' },
];

const weekDays = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
}

export function SLASettings() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<SLAPolicy | null>(null);
  const [policyForm, setPolicyForm] = useState<CreatePolicyData>({
    name: '',
    description: '',
    is_default: false,
    rules: [],
  });
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [holidayForm, setHolidayForm] = useState({
    date: '',
    name: '',
    is_recurring: false,
  });

  // Fetch organizations
  const { data: organizationsData } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await organizationsService.getOrganizations();
      return response;
    },
  });

  // Ensure organizations is always an array
  const organizations = Array.isArray(organizationsData?.data)
    ? organizationsData.data
    : [];

  // Set default organization when loaded
  useEffect(() => {
    if (organizations.length > 0 && !selectedOrganizationId) {
      setSelectedOrganizationId(organizations[0].id);
    }
  }, [organizations, selectedOrganizationId]);

  // Queries - with organization filter
  const { data: policies = [], isLoading: loadingPolicies } = useQuery({
    queryKey: ['sla-policies', selectedOrganizationId],
    queryFn: async () => {
      const response = await slaService.getPolicies(selectedOrganizationId);
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!selectedOrganizationId,
  });

  const { data: businessHours = [], isLoading: loadingHours } = useQuery({
    queryKey: ['business-hours', selectedOrganizationId],
    queryFn: async () => {
      const response = await slaService.getBusinessHours(selectedOrganizationId);
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!selectedOrganizationId,
  });

  const { data: metrics, isLoading: loadingMetrics } = useQuery({
    queryKey: ['sla-metrics', selectedOrganizationId],
    queryFn: async () => {
      const response = await slaService.getMetrics(30, selectedOrganizationId);
      return response.data || null;
    },
    enabled: !!selectedOrganizationId,
  });

  const { data: holidays = [], isLoading: loadingHolidays } = useQuery({
    queryKey: ['sla-holidays', selectedOrganizationId],
    queryFn: async () => {
      const response = await slaService.getHolidays(selectedOrganizationId);
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!selectedOrganizationId,
  });

  // Mutations
  const createPolicyMutation = useMutation({
    mutationFn: (data: CreatePolicyData) => slaService.createPolicy(data, selectedOrganizationId),
    onSuccess: () => {
      queryClient.invalidateQueries(['sla-policies', selectedOrganizationId]);
      setShowPolicyDialog(false);
      resetPolicyForm();
    },
  });

  const updatePolicyMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePolicyData }) =>
      slaService.updatePolicy(id, data, selectedOrganizationId),
    onSuccess: () => {
      queryClient.invalidateQueries(['sla-policies', selectedOrganizationId]);
      setShowPolicyDialog(false);
      setEditingPolicy(null);
      resetPolicyForm();
    },
  });

  const deletePolicyMutation = useMutation({
    mutationFn: (id: string) => slaService.deletePolicy(id, selectedOrganizationId),
    onSuccess: () => {
      queryClient.invalidateQueries(['sla-policies', selectedOrganizationId]);
    },
    onError: (error) => {
      console.error('Failed to delete policy:', error);
    },
  });

  const updateBusinessHoursMutation = useMutation({
    mutationFn: (hours: BusinessHours[]) => slaService.updateBusinessHours(hours, selectedOrganizationId),
    onSuccess: () => {
      queryClient.invalidateQueries(['business-hours', selectedOrganizationId]);
    },
  });

  const createHolidayMutation = useMutation({
    mutationFn: (data: { date: string; name: string; is_recurring?: boolean }) =>
      slaService.addHoliday(data, selectedOrganizationId),
    onSuccess: () => {
      queryClient.invalidateQueries(['sla-holidays', selectedOrganizationId]);
      setShowHolidayDialog(false);
      resetHolidayForm();
    },
  });

  const updateHolidayMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { date: string; name: string; is_recurring?: boolean } }) =>
      slaService.updateHoliday(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['sla-holidays', selectedOrganizationId]);
      setShowHolidayDialog(false);
      setEditingHoliday(null);
      resetHolidayForm();
    },
  });

  const deleteHolidayMutation = useMutation({
    mutationFn: (id: string) => slaService.deleteHoliday(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['sla-holidays', selectedOrganizationId]);
    },
  });

  const resetPolicyForm = () => {
    setPolicyForm({
      name: '',
      description: '',
      is_default: false,
      rules: [],
    });
  };

  const resetHolidayForm = () => {
    setHolidayForm({
      date: '',
      name: '',
      is_recurring: false,
    });
  };

  const handleEditHoliday = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setHolidayForm({
      date: holiday.date.split('T')[0],
      name: holiday.name,
      is_recurring: holiday.is_recurring,
    });
    setShowHolidayDialog(true);
  };

  const handleSaveHoliday = () => {
    if (editingHoliday) {
      updateHolidayMutation.mutate({
        id: editingHoliday.id,
        data: holidayForm,
      });
    } else {
      createHolidayMutation.mutate(holidayForm);
    }
  };

  const policyCount = policies.length;
  const activePolicies = policies.filter((policy) => policy.is_active).length;
  const inactivePolicies = Math.max(0, policyCount - activePolicies);
  const defaultPolicy = policies.find((policy) => policy.is_default);

  const handleEditPolicy = (policy: SLAPolicy) => {
    setEditingPolicy(policy);
    // Load policy details with rules
    slaService.getPolicyById(policy.id, selectedOrganizationId)
      .then((response) => {
        setPolicyForm({
          name: policy.name,
          description: policy.description || '',
          is_default: policy.is_default,
          rules: response.data?.rules || [],
        });
        setShowPolicyDialog(true);
      })
      .catch((error) => {
        console.error('Failed to load policy details:', error);
        // Still open dialog with basic info even if rules fail to load
        setPolicyForm({
          name: policy.name,
          description: policy.description || '',
          is_default: policy.is_default,
          rules: [],
        });
        setShowPolicyDialog(true);
      });
  };

  const handleSavePolicy = () => {
    if (editingPolicy) {
      updatePolicyMutation.mutate({
        id: editingPolicy.id,
        data: { ...policyForm, is_active: editingPolicy.is_active },
      });
    } else {
      createPolicyMutation.mutate(policyForm);
    }
  };

  const addRule = () => {
    setPolicyForm({
      ...policyForm,
      rules: [
        ...(policyForm.rules || []),
        {
          priority: 'normal',
          first_response_time: 60,
          resolution_time: 240,
          business_hours_only: false,
        },
      ],
    });
  };

  const updateRule = (index: number, rule: Partial<SLARule>) => {
    const rules = [...(policyForm.rules || [])];
    rules[index] = { ...rules[index], ...rule };
    setPolicyForm({ ...policyForm, rules });
  };

  const removeRule = (index: number) => {
    const rules = [...(policyForm.rules || [])];
    rules.splice(index, 1);
    setPolicyForm({ ...policyForm, rules });
  };

  return (
    <div className="space-y-8 font-sans">
      {/* Organization Selector */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-light text-slate-900 tracking-tight">{t('common:x3.slaManagement', 'SLA Management')}</h2>
          <p className="text-sm text-slate-500 mt-1">
            {t('common:x3.slaManagementDesc', 'Service Level Agreements & Reaktionszeiten verwalten')}
          </p>
        </div>
        <div className="flex items-center gap-3 bg-white p-1 rounded-lg border border-slate-100">
          <Building2 className="h-4 w-4 text-slate-400 ml-2" />
          <Select
            value={selectedOrganizationId}
            onValueChange={setSelectedOrganizationId}
          >
            <SelectTrigger className="w-[200px] border-0 h-9 bg-transparent focus:ring-0 text-slate-600 text-sm font-medium">
              <SelectValue placeholder={t('views:u.phSelectOrg', 'Organisation wählen...')} />
            </SelectTrigger>
            <SelectContent>
              {organizations.map((org: any) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="policies" className="w-full space-y-6">
        <TabsList className="bg-transparent p-0 border-b border-slate-200 w-full justify-start h-auto gap-6 rounded-none">
          <TabsTrigger
            value="policies"
            className="rounded-none border-b-2 border-transparent bg-transparent px-0 py-3 text-sm font-medium text-slate-500 data-[state=active]:border-[#0F99FD] data-[state=active]:text-slate-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-slate-700 transition-colors"
          >
            <Settings className="mr-2 h-4 w-4" />
            Policies
          </TabsTrigger>
          <TabsTrigger
            value="business-hours"
            className="rounded-none border-b-2 border-transparent bg-transparent px-0 py-3 text-sm font-medium text-slate-500 data-[state=active]:border-[#0F99FD] data-[state=active]:text-slate-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-slate-700 transition-colors"
          >
            <Calendar className="mr-2 h-4 w-4" />
            Business Hours
          </TabsTrigger>
          <TabsTrigger
            value="metrics"
            className="rounded-none border-b-2 border-transparent bg-transparent px-0 py-3 text-sm font-medium text-slate-500 data-[state=active]:border-[#0F99FD] data-[state=active]:text-slate-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-slate-700 transition-colors"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Performance
          </TabsTrigger>
          <TabsTrigger
            value="holidays"
            className="rounded-none border-b-2 border-transparent bg-transparent px-0 py-3 text-sm font-medium text-slate-500 data-[state=active]:border-[#0F99FD] data-[state=active]:text-slate-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-slate-700 transition-colors"
          >
            <CalendarDays className="mr-2 h-4 w-4" />
            {t('common:x1.holidays', 'Feiertage')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="policies" className="space-y-6">
          <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                   <h3 className="text-base font-semibold text-slate-900">{t('common:x3.policiesOverview', 'Policies Übersicht')}</h3>
                   <span className="text-xs text-slate-400 font-normal ml-2">({activePolicies} aktiv)</span>
                </div>
                <p className="text-sm text-slate-500">
                  {t('common:x3.definePolicyTimes', 'Definieren Sie Antwort- und Lösungszeiten für Prioritäten.')}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-9 gap-2 text-slate-600 hover:text-slate-900 border-slate-200"
                onClick={() => {
                  resetPolicyForm();
                  setEditingPolicy(null);
                  setShowPolicyDialog(true);
                }}
              >
                <Plus className="h-4 w-4" />
                {t('common:x3.newPolicy', 'Neue Policy')}
              </Button>
            </CardHeader>
            <CardContent>
              {policyCount === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center text-slate-500">
                  <div className="h-12 w-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
                    <Clock className="h-6 w-6 text-slate-300" />
                  </div>
                  <p className="text-sm font-medium text-slate-900">{t('common:x3.noSlaPolicy', 'Keine SLA Policy gefunden')}</p>
                  <p className="text-xs text-slate-500 mt-1 max-w-sm">
                    Erstellen Sie eine neue Policy, um SLAs für verschiedene Ticket-Prioritäten zu definieren.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {policies.map((policy) => (
                    <div
                      key={policy.id}
                      className="group py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/50 -mx-6 px-6 transition-colors"
                    >
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-slate-900">{policy.name}</span>
                          {policy.is_default && (
                            <Badge variant="secondary" className="bg-emerald-50 text-emerald-600 border-0 text-[10px] font-medium">
                              {t('common:x1.standard', 'Standard')}
                            </Badge>
                          )}
                          {!policy.is_active && (
                            <Badge variant="secondary" className="bg-slate-100 text-slate-500 border-0 text-[10px] font-medium">
                              {t('common:state.inactive', 'Inaktiv')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 max-w-xl">
                          {policy.description || 'Keine Beschreibung.'}
                        </p>
                        <div className="flex items-center gap-4 text-[11px] text-slate-400">
                          <span className="flex items-center gap-1.5">
                             <div className="w-1 h-1 rounded-full bg-slate-300"></div>
                             {policy.rules_count || 0} Regeln
                          </span>
                          <span className="flex items-center gap-1.5">
                             <div className="w-1 h-1 rounded-full bg-slate-300"></div>
                             {policy.active_tickets_count || 0} Tickets
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-slate-900"
                          onClick={() => handleEditPolicy(policy)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50"
                                onClick={() => deletePolicyMutation.mutate(policy.id)}
                                disabled={policy.is_default}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {policy.is_default ? 'Standard-Policy kann nicht gelöscht werden' : 'Löschen'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="business-hours" className="space-y-4">
          <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
            <CardHeader className="pb-2">
              <h3 className="text-base font-semibold text-slate-900">{t('common:x3.workingHours', 'Arbeitszeiten')}</h3>
              <p className="text-sm text-slate-500">
                {t('common:x3.defineSlaActive', 'Definieren Sie, wann die SLA-Zeitmessung aktiv ist.')}
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 mt-2">
                {businessHours.map((day) => {
                  const label = weekDays.find((d) => d.value === day.day_of_week)?.label || 'Day';
                  return (
                    <div
                      key={day.day_of_week}
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50/30"
                    >
                      <div className="flex items-center gap-4">
                         <div className="w-24 text-sm font-medium text-slate-700">{label}</div>
                         <Switch
                          checked={day.is_working_day}
                          onCheckedChange={(checked) => {
                            const updated = businessHours.map((h) =>
                              h.day_of_week === day.day_of_week ? { ...h, is_working_day: checked } : h
                            );
                            updateBusinessHoursMutation.mutate(updated);
                          }}
                          className="data-[state=checked]:bg-[#0F99FD]"
                        />
                      </div>
                      
                      {day.is_working_day ? (
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 bg-white px-2 py-1 rounded border border-slate-200">
                            <Input
                              type="time"
                              value={day.start_time}
                              onChange={(e) => {
                                const updated = businessHours.map((h) =>
                                  h.day_of_week === day.day_of_week
                                    ? { ...h, start_time: `${e.target.value}:00` }
                                    : h
                                );
                                updateBusinessHoursMutation.mutate(updated);
                              }}
                              className="h-6 w-24 border-none bg-transparent p-0 text-sm focus-visible:ring-0 text-slate-600 font-mono text-center"
                            />
                          </div>
                          <span className="text-slate-400">-</span>
                          <div className="flex items-center gap-2 bg-white px-2 py-1 rounded border border-slate-200">
                            <Input
                              type="time"
                              value={day.end_time}
                              onChange={(e) => {
                                const updated = businessHours.map((h) =>
                                  h.day_of_week === day.day_of_week
                                    ? { ...h, end_time: `${e.target.value}:00` }
                                    : h
                                );
                                updateBusinessHoursMutation.mutate(updated);
                              }}
                              className="h-6 w-24 border-none bg-transparent p-0 text-sm focus-visible:ring-0 text-slate-600 font-mono text-center"
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 font-medium px-4">{t('domain:status.closed', 'Geschlossen')}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metrics" className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
              <CardContent className="p-6">
                 <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{t('common:x3.totalTickets', 'Gesamt Tickets')}</div>
                 <div className="text-3xl font-light text-slate-900 tracking-tight">{metrics?.total_tickets || 0}</div>
              </CardContent>
            </Card>

            <Card className="border-0 ring-1 ring-emerald-100 bg-white">
              <CardContent className="p-6">
                 <div className="text-xs font-medium uppercase tracking-wider text-emerald-600 mb-1">First Response</div>
                 <div className="text-3xl font-light text-slate-900 tracking-tight flex items-baseline gap-2">
                   {metrics?.first_response_rate || 0}<span className="text-lg text-slate-400">%</span>
                 </div>
                 <div className="mt-2 flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1 text-emerald-600 font-medium">
                      <CheckCircle className="h-3 w-3" /> {metrics?.first_response_achieved || 0}
                    </span>
                    <span className="flex items-center gap-1 text-rose-500 font-medium">
                      <XCircle className="h-3 w-3" /> {metrics?.first_response_breached || 0}
                    </span>
                 </div>
              </CardContent>
            </Card>

            <Card className="border-0 ring-1 ring-sky-100 bg-white">
              <CardContent className="p-6">
                 <div className="text-xs font-medium uppercase tracking-wider text-sky-600 mb-1">Resolution Rate</div>
                 <div className="text-3xl font-light text-slate-900 tracking-tight flex items-baseline gap-2">
                   {metrics?.resolution_rate || 0}<span className="text-lg text-slate-400">%</span>
                 </div>
                 <div className="mt-2 flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1 text-emerald-600 font-medium">
                      <CheckCircle className="h-3 w-3" /> {metrics?.resolution_achieved || 0}
                    </span>
                    <span className="flex items-center gap-1 text-rose-500 font-medium">
                      <XCircle className="h-3 w-3" /> {metrics?.resolution_breached || 0}
                    </span>
                 </div>
              </CardContent>
            </Card>

            <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
               <CardContent className="p-6">
                 <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">Ø Zeiten</div>
                 <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                       <span className="text-slate-500">Response</span>
                       <span className="font-medium text-slate-900">{formatMinutes(metrics?.avg_first_response_minutes || 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                       <span className="text-slate-500">Resolution</span>
                       <span className="font-medium text-slate-900">{formatMinutes(metrics?.avg_resolution_minutes || 0)}</span>
                    </div>
                 </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="holidays" className="space-y-4">
          <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="space-y-1">
                 <h3 className="text-base font-semibold text-slate-900">{t('common:x1.holidays', 'Feiertage')}</h3>
                 <p className="text-sm text-slate-500">
                    {t('common:x3.slaPauseDays', 'An diesen Tagen pausiert die SLA-Berechnung automatisch.')}
                 </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-9 gap-2 text-slate-600 hover:text-slate-900 border-slate-200"
                onClick={() => {
                    resetHolidayForm();
                    setEditingHoliday(null);
                    setShowHolidayDialog(true);
                }}
              >
                <Plus className="h-4 w-4" />
                {t('common:x3.newHoliday', 'Neuer Feiertag')}
              </Button>
            </CardHeader>
            <CardContent>
              {loadingHolidays ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-5 w-5 animate-spin text-slate-300" />
                </div>
              ) : holidays.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center text-slate-500">
                  <div className="h-12 w-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
                    <CalendarDays className="h-6 w-6 text-slate-300" />
                  </div>
                  <p className="text-sm font-medium text-slate-900">{t('common:x3.noHolidays', 'Keine Feiertage konfiguriert')}</p>
                </div>
              ) : (
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {holidays
                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                    .map((holiday) => (
                      <div
                        key={holiday.id}
                        className="group relative flex items-start p-4 rounded-xl border border-slate-100 bg-slate-50/30 hover:bg-white hover:border-slate-200 transition-all"
                      >
                        <div className="flex-1 flex gap-4">
                           <div className="flex flex-col items-center justify-center w-12 h-12 rounded-lg bg-white border border-slate-100 text-slate-900">
                              <span className="text-[10px] uppercase font-bold text-slate-400">
                                {formatCustom(holiday.date, { month: 'short' }).replace('.', '')}
                              </span>
                              <span className="text-xl font-light tracking-tighter">
                                {new Date(holiday.date).getDate()}
                              </span>
                           </div>
                           <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-900 text-sm">{holiday.name}</span>
                                {holiday.is_recurring && (
                                   <RefreshCw className="h-3 w-3 text-slate-400" />
                                )}
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {formatCustom(holiday.date, { weekday: 'long', year: 'numeric' })}
                              </p>
                           </div>
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-slate-400 hover:text-slate-900"
                              onClick={() => handleEditHoliday(holiday)}
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-slate-400 hover:text-red-600"
                              onClick={() => deleteHolidayMutation.mutate(holiday.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Holiday Dialog */}
      <Dialog open={showHolidayDialog} onOpenChange={setShowHolidayDialog}>
        <DialogContent className="sm:max-w-[420px] p-0 overflow-hidden border-0 rounded-2xl">
           <div className="bg-slate-50 px-6 py-4 border-b border-slate-100">
              <DialogTitle className="text-base font-semibold text-slate-900">
                {editingHoliday ? 'Feiertag bearbeiten' : 'Neuer Feiertag'}
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 mt-1">
                {t('common:x3.dayExcludedFromSla', 'Dieser Tag wird aus der SLA-Berechnung ausgeschlossen.')}
              </DialogDescription>
           </div>
          <div className="p-6 space-y-4 bg-white">
            <div className="space-y-2">
              <Label className="text-xs font-medium text-slate-700">{t('ticket:ui.date', 'Datum')}</Label>
              <Input
                type="date"
                value={holidayForm.date}
                onChange={(e) => setHolidayForm({ ...holidayForm, date: e.target.value })}
                className="h-9 border-slate-200 focus-visible:ring-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium text-slate-700">{t('common:x3.designation', 'Bezeichnung')}</Label>
              <Input
                value={holidayForm.name}
                onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                placeholder={t('common:x3.egNewYear', 'z. B. Neujahr')}
                 className="h-9 border-slate-200 focus-visible:ring-slate-200"
              />
            </div>
            <div className="flex items-center justify-between pt-2">
               <div className="flex flex-col">
                  <span className="text-sm font-medium text-slate-700">{t('common:x3.repeatYearly', 'Jährlich wiederholen')}</span>
                  <span className="text-xs text-slate-500">{t('common:x3.appliesAllYears', 'Gilt für alle kommenden Jahre')}</span>
               </div>
              <Switch
                checked={holidayForm.is_recurring}
                onCheckedChange={(checked) => setHolidayForm({ ...holidayForm, is_recurring: checked })}
                className="data-[state=checked]:bg-slate-900"
              />
            </div>
          </div>
          <DialogFooter className="bg-slate-50 px-6 py-3 border-t border-slate-100 gap-2">
            <Button
              variant="outline"
              onClick={() => setShowHolidayDialog(false)}
              className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
            >
              {t('common:action.cancel', 'Abbrechen')}
            </Button>
            <Button
              onClick={handleSaveHoliday}
              disabled={!holidayForm.date || !holidayForm.name}
              className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
            >
              {t('common:action.save', 'Speichern')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Policy Dialog */}
      <Dialog open={showPolicyDialog} onOpenChange={setShowPolicyDialog}>
        <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
          <div className="flex flex-1 overflow-hidden">
             {/* Sidebar */}
            <div className="bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-4 w-[240px] flex-shrink-0">
              <div>
                 <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-slate-900">{t('common:x3.slaPolicy', 'SLA Policy')}</span>
                 </div>
                 <p className="text-xs text-slate-500 leading-relaxed">
                   Konfigurieren Sie Regeln für verschiedene Prioritätsstufen. Diese bestimmen, wann Tickets als überfällig markiert werden.
                 </p>
              </div>

              {editingPolicy && (
                <div className="mt-auto rounded-xl bg-white p-4 border border-slate-100">
                  <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">{t('common:x3.current', 'Aktuell')}</span>
                  <span className="text-sm font-medium text-slate-900 block truncate">{editingPolicy.name}</span>
                  <div className="flex gap-2 mt-2">
                     <span className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600">{editingPolicy.rules_count || 0} Regeln</span>
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 bg-white min-w-0">
              <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                 <DialogTitle className="text-base font-semibold text-slate-900">
                  {editingPolicy ? 'Policy bearbeiten' : 'Neue Policy erstellen'}
                </DialogTitle>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6">
                 <div className="space-y-6 max-w-2xl">
                    <div className="grid gap-4">
                       <div className="space-y-2">
                          <Label className="text-xs font-medium text-slate-700">{t('common:x3.policyName', 'Name der Policy')}</Label>
                          <Input
                            value={policyForm.name}
                            onChange={(e) => setPolicyForm({ ...policyForm, name: e.target.value })}
                            placeholder={t('common:x3.egVipSla', 'z. B. VIP Support SLA')}
                            className="h-9 border-slate-200 focus-visible:ring-slate-200"
                          />
                       </div>
                       
                       <div className="space-y-2">
                          <Label className="text-xs font-medium text-slate-700">{t('common:field.descriptionOptional', 'Beschreibung (Optional)')}</Label>
                          <Textarea
                            value={policyForm.description}
                            onChange={(e) => setPolicyForm({ ...policyForm, description: e.target.value })}
                            placeholder={t('common:x3.policyUsedFor', 'Wofür wird diese Policy verwendet?')}
                            className="min-h-[60px] border-slate-200 focus-visible:ring-slate-200 resize-none text-sm"
                          />
                       </div>

                       <div className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50/50">
                          <div className="flex flex-col">
                             <span className="text-sm font-medium text-slate-700">{t('common:x3.setAsDefault', 'Als Standard festlegen')}</span>
                             <span className="text-xs text-slate-500">{t('common:x3.newTicketsGetPolicy', 'Neue Tickets erhalten automatisch diese Policy')}</span>
                          </div>
                          <Switch
                            checked={policyForm.is_default}
                            onCheckedChange={(checked) => setPolicyForm({ ...policyForm, is_default: checked })}
                            className="data-[state=checked]:bg-emerald-500"
                          />
                       </div>
                    </div>

                    <div className="space-y-4 pt-4 border-t border-slate-100">
                       <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-slate-900">{t('common:x3.priorityRules', 'Prioritäts-Regeln')}</h4>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1.5 text-xs border-slate-200"
                            onClick={addRule}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            {t('common:x3.addRule', 'Regel hinzufügen')}
                          </Button>
                       </div>

                       {policyForm.rules?.length === 0 ? (
                          <div className="text-center py-8 border border-dashed border-slate-200 rounded-xl">
                             <p className="text-xs text-slate-500">{t('common:x3.noRules', 'Keine Regeln definiert')}</p>
                          </div>
                       ) : (
                          <div className="space-y-3">
                             {policyForm.rules?.map((rule, index) => (
                                <div key={index} className="p-4 rounded-xl border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 space-y-4 relative group">
                                   <Button
                                      size="icon"
                                      variant="ghost"
                                      className="absolute top-2 right-2 h-6 w-6 text-slate-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                                      onClick={() => removeRule(index)}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>

                                   <div className="grid grid-cols-2 gap-4">
                                      <div className="space-y-1.5">
                                         <Label className="text-[10px] uppercase font-bold text-slate-400">{t('ticketList:column.priority', 'Priorität')}</Label>
                                         <Select
                                            value={rule.priority}
                                            onValueChange={(value) => updateRule(index, { priority: value as any })}
                                          >
                                            <SelectTrigger className="h-8 text-xs">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {priorities.map((p) => (
                                                <SelectItem key={p.value} value={p.value}>
                                                  {p.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                      </div>
                                      <div className="flex items-end">
                                          <div className="flex items-center gap-2">
                                             <Switch
                                                checked={rule.business_hours_only}
                                                onCheckedChange={(checked) =>
                                                  updateRule(index, { business_hours_only: checked })
                                                }
                                                className="scale-75 origin-left data-[state=checked]:bg-slate-900"
                                              />
                                              <span className="text-xs text-slate-600">{t('common:x3.onlyWorkingHours', 'Nur Arbeitszeiten')}</span>
                                          </div>
                                      </div>
                                   </div>
                                   <div className="grid grid-cols-2 gap-4">
                                       <div className="space-y-1.5">
                                          <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:x1.firstResponse', 'Erste Antwort')}</Label>
                                          <div className="relative">
                                             <Input
                                                type="number"
                                                value={rule.first_response_time}
                                                onChange={(e) =>
                                                  updateRule(index, {
                                                    first_response_time: parseInt(e.target.value),
                                                  })
                                                }
                                                min={1}
                                                className="h-8 text-xs pr-8"
                                              />
                                              <span className="absolute right-2 top-2 text-[10px] text-slate-400">min</span>
                                          </div>
                                       </div>
                                       <div className="space-y-1.5">
                                          <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:x1.resolution', 'Lösung')}</Label>
                                          <div className="relative">
                                             <Input
                                                type="number"
                                                value={rule.resolution_time}
                                                onChange={(e) =>
                                                  updateRule(index, {
                                                    resolution_time: parseInt(e.target.value),
                                                  })
                                                }
                                                min={1}
                                                className="h-8 text-xs pr-8"
                                              />
                                              <span className="absolute right-2 top-2 text-[10px] text-slate-400">min</span>
                                          </div>
                                       </div>
                                   </div>
                                </div>
                             ))}
                          </div>
                       )}
                    </div>
                 </div>
              </div>
              
              <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => setShowPolicyDialog(false)}
                    className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                  >
                    {t('common:action.cancel', 'Abbrechen')}
                  </Button>
                  <Button
                    onClick={handleSavePolicy}
                    className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                  >
                    {t('common:action.save', 'Speichern')}
                  </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
